import type {ChatSnapshot} from '../chat-state';
import type {
    ChatStatusItem,
    WorkspaceContextUploadStatusItem,
} from '../chat-status';
import {
    isWorkspaceContextUploadStatusItem,
} from '../chat-status';

declare const howspace: any;

const workspaceContextUserVisibleErrorTranslationKeys = {
    processing_failed: 'workspace_context_user_visible_error_processing_failed',
    source_file_too_large: 'workspace_context_user_visible_error_source_file_too_large',
    too_many_assets: 'workspace_context_user_visible_error_too_many_assets',
    asset_too_large: 'workspace_context_user_visible_error_asset_too_large',
} as const;

type WorkspaceContextUploadActionMessageTranslationKey = (
    | 'chatbot_context_file_generate_page_message'
    | 'chatbot_context_file_create_widget_message'
);

type StatusAreaTone = 'queued' | 'processing' | 'ready' | 'failed' | 'error';
type StatusAreaLabel = string | (string | Node)[];
type StatusAreaAction = {
    label: string;
    disabled?: boolean;
    onClick: (button: HTMLButtonElement) => void;
};
type StatusAreaItem = {
    key: string;
    tone: StatusAreaTone;
    label: StatusAreaLabel;
    actions?: StatusAreaAction[];
    dismissible?: boolean;
    expandable?: boolean;
};

type ChatStatusAreaLabels = {
    close: string;
    expandStatus: string;
    collapseStatus: string;
    runTimeoutWarning: string;
    contextFileQueued: string;
    contextFileProcessing: string;
    contextFileReady: string;
    contextFileFailed: string;
    contextFileGeneratePageAction: string;
    contextFileCreateWidgetAction: string;
    contextFileMaxSourceFileSize: string;
    contextFileMaxCreatedAssetCount: string;
    contextFileMaxCreatedAssetFileSize: string;
};

type ChatStatusAreaOptions = {
    labels: ChatStatusAreaLabels;
    isRunning: () => boolean;
    onSubmit: (message: string) => void;
    focusComposer: () => void;
    setContextUploadBusy: (busy: boolean) => void;
};

export class ChatStatusArea {
    private signature = '';
    private readonly dismissedStatusItemIds = new Set<string>();
    private readonly expandedStatusItemIds = new Set<string>();

    constructor(
        private readonly element: HTMLElement,
        private readonly options: ChatStatusAreaOptions,
    ) {
    }

    render(snapshot: Pick<ChatSnapshot, 'error' | 'errorCode' | 'statusItems'>): void {
        const errorText = snapshot.error
            ? (snapshot.errorCode === 'agent_run_timeout'
                ? this.options.labels.runTimeoutWarning || snapshot.error
                : snapshot.error)
            : '';
        const statusItems = (snapshot.statusItems || [])
            .filter(item => !(item.status === 'ready' && this.dismissedStatusItemIds.has(item.id)));
        this.options.setContextUploadBusy(statusItems.some(item => (
            isWorkspaceContextUploadStatusItem(item)
            && (item.status === 'queued' || item.status === 'processing')
        )));
        const items = this.statusAreaItems(errorText, statusItems);

        const signature = [
            snapshot.errorCode || '',
            this.options.isRunning() ? 'running' : 'idle',
            items.map(statusAreaItemFingerprint).join('\u0002'),
        ].join('\u0000');
        if (this.signature === signature) {
            return;
        }

        this.signature = signature;
        this.element.replaceChildren();
        if (items.length === 0) {
            return;
        }

        items.forEach(item => {
            const element = this.renderStatusAreaItem(item);
            this.element.append(element);
            this.enableExpansionWhenTextOverflows(item, element);
        });
    }

    private statusAreaItems(errorText: string, statusItems: ChatStatusItem[]): StatusAreaItem[] {
        const items: StatusAreaItem[] = [];
        if (errorText) {
            items.push({
                key: `error:${errorText}`,
                tone: 'error',
                label: errorText,
                expandable: true,
            });
        }

        statusItems.forEach(item => {
            if (isWorkspaceContextUploadStatusItem(item)) {
                items.push(this.workspaceContextUploadStatusAreaItem(item));
            }
        });

        return items;
    }

    private workspaceContextUploadStatusAreaItem(item: WorkspaceContextUploadStatusItem): StatusAreaItem {
        const userVisibleErrorMessage = this.workspaceContextUserVisibleErrorMessage(item);

        return {
            key: item.id,
            tone: item.status,
            label: item.status === 'ready'
                ? this.workspaceContextReadyLabel(item)
                : this.workspaceContextUploadStatusLabel(item, userVisibleErrorMessage),
            dismissible: item.status === 'ready',
            expandable: userVisibleErrorMessage !== '',
            actions: item.status === 'ready' ? this.workspaceContextUploadActions(item) : [],
        };
    }

    private renderStatusAreaItem(statusItem: StatusAreaItem): HTMLElement {
        const actions = statusItem.actions ?? [];
        const item = document.createElement('div');
        item.className = `howspace-ai-chat-status-item is-${statusItem.tone}`;
        item.setAttribute('role', statusItem.tone === 'failed' || statusItem.tone === 'error' ? 'alert' : 'status');
        if (actions.length > 0) {
            item.classList.add('has-actions');
        }
        if (statusItem.expandable) {
            item.classList.add('is-expandable');
        }
        if (this.expandedStatusItemIds.has(statusItem.key)) {
            item.classList.add('is-expanded');
        }

        const indicator = document.createElement('span');
        indicator.className = 'howspace-ai-chat-status-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        if (statusItem.tone === 'queued' || statusItem.tone === 'processing') {
            indicator.append(
                document.createElement('span'),
                document.createElement('span'),
                document.createElement('span'),
            );
        } else if (statusItem.tone === 'ready') {
            indicator.innerHTML = '<i class="fa-light fa-circle-check" aria-hidden="true"></i>';
        }

        const text = document.createElement('span');
        text.className = 'howspace-ai-chat-status-text';
        this.appendStatusAreaLabel(text, statusItem.label);

        item.append(indicator, text);
        if (statusItem.expandable) {
            item.append(this.renderExpandStatusButton(statusItem.key));
        }
        if (statusItem.dismissible) {
            item.append(this.renderDismissStatusButton(statusItem.key));
        }
        if (actions.length > 0) {
            item.append(this.renderStatusAreaActions(actions));
        }

        return item;
    }

    private enableExpansionWhenTextOverflows(statusItem: StatusAreaItem, item: HTMLElement): void {
        if (statusItem.expandable || item.querySelector('.howspace-ai-chat-status-expand') !== null) {
            return;
        }

        const text = item.querySelector('.howspace-ai-chat-status-text');
        if (!(text instanceof HTMLElement) || text.scrollWidth <= text.clientWidth + 1) {
            return;
        }

        item.classList.add('is-expandable');
        const button = this.renderExpandStatusButton(statusItem.key);
        const dismissButton = item.querySelector('.howspace-ai-chat-status-dismiss');
        const actions = item.querySelector('.howspace-ai-chat-status-actions');
        item.insertBefore(button, dismissButton ?? actions);
    }

    private renderExpandStatusButton(statusItemId: string): HTMLButtonElement {
        const expanded = this.expandedStatusItemIds.has(statusItemId);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-chat-status-expand';
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        button.setAttribute(
            'aria-label',
            expanded
                ? (this.options.labels.collapseStatus || 'Show less')
                : (this.options.labels.expandStatus || 'Show full message'),
        );
        button.innerHTML = '<i class="fa-solid fa-chevron-right" aria-hidden="true"></i>';
        button.addEventListener('click', () => {
            this.toggleStatusAreaItemExpansion(statusItemId, button);
        });

        return button;
    }

    private appendStatusAreaLabel(element: HTMLElement, label: StatusAreaLabel): void {
        if (typeof label === 'string') {
            element.textContent = label;
            return;
        }

        label.forEach(part => {
            element.append(part);
        });
    }

    private renderDismissStatusButton(statusItemId: string): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-chat-status-dismiss';
        button.setAttribute('aria-label', this.options.labels.close || 'Close');
        button.innerHTML = '<i class="fa-light fa-xmark" aria-hidden="true"></i>';
        button.addEventListener('click', () => {
            this.dismissStatusAreaItem(statusItemId, button);
        });

        return button;
    }

    private dismissStatusAreaItem(statusItemId: string, source: HTMLElement): void {
        this.dismissedStatusItemIds.add(statusItemId);
        const item = source.closest('.howspace-ai-chat-status-item');
        item?.remove();
    }

    private toggleStatusAreaItemExpansion(statusItemId: string, source: HTMLButtonElement): void {
        const expanded = !this.expandedStatusItemIds.has(statusItemId);
        if (expanded) {
            this.expandedStatusItemIds.add(statusItemId);
        } else {
            this.expandedStatusItemIds.delete(statusItemId);
        }

        const item = source.closest('.howspace-ai-chat-status-item');
        item?.classList.toggle('is-expanded', expanded);
        source.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        source.setAttribute(
            'aria-label',
            expanded
                ? (this.options.labels.collapseStatus || 'Show less')
                : (this.options.labels.expandStatus || 'Show full message'),
        );
    }

    private renderStatusAreaActions(statusActions: StatusAreaAction[]): HTMLElement {
        const actions = document.createElement('div');
        actions.className = 'howspace-ai-chat-status-actions';
        actions.append(...statusActions.map(action => this.renderStatusAreaActionButton(action)));

        return actions;
    }

    private renderStatusAreaActionButton(action: StatusAreaAction): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-chat-status-action';
        button.textContent = action.label;
        button.disabled = action.disabled === true;
        button.addEventListener('click', () => {
            if (button.disabled) {
                return;
            }

            action.onClick(button);
        });

        return button;
    }

    private workspaceContextUploadActions(item: WorkspaceContextUploadStatusItem): StatusAreaAction[] {
        return [
            this.workspaceContextUploadAction(
                this.options.labels.contextFileGeneratePageAction || 'Generate a page from this file',
                'chatbot_context_file_generate_page_message',
                item,
            ),
            this.workspaceContextUploadAction(
                this.options.labels.contextFileCreateWidgetAction || 'Create a widget from this file',
                'chatbot_context_file_create_widget_message',
                item,
            ),
        ];
    }

    private workspaceContextUploadAction(
        label: string,
        messageTranslationKey: WorkspaceContextUploadActionMessageTranslationKey,
        item: WorkspaceContextUploadStatusItem,
    ): StatusAreaAction {
        return {
            label,
            disabled: this.options.isRunning(),
            onClick: button => {
                if (this.options.isRunning()) {
                    return;
                }

                const message = this.workspaceContextUploadActionMessage(messageTranslationKey, item);
                this.options.onSubmit(message);
                this.dismissStatusAreaItem(item.id, button);
                this.options.focusComposer();
            },
        };
    }

    private workspaceContextReadyLabel(item: WorkspaceContextUploadStatusItem): StatusAreaLabel {
        const title = this.workspaceContextUploadTitle(item);
        const template = this.options.labels.contextFileReady || '%title% is ready';
        const marker = template.includes('%title%')
            ? '%title%'
            : (template.includes('%filename%') ? '%filename%' : '');

        if (marker === '') {
            return [this.renderStatusAreaStrong(title), ' is ready'];
        }

        const [before, after] = template.split(marker);
        return [before, this.renderStatusAreaStrong(title), after];
    }

    private renderStatusAreaStrong(text: string): HTMLElement {
        const strong = document.createElement('strong');
        strong.textContent = text;
        return strong;
    }

    private workspaceContextUploadActionMessage(
        translationKey: WorkspaceContextUploadActionMessageTranslationKey,
        item: WorkspaceContextUploadStatusItem,
    ): string {
        const template = this.workspaceContextUploadActionMessageTemplate(translationKey);
        const title = this.workspaceContextUploadTitle(item);
        const filename = item.filename || title;
        return template
            .replace(/%title%/g, title)
            .replace(/%filename%/g, filename);
    }

    private workspaceContextUploadActionMessageTemplate(
        translationKey: WorkspaceContextUploadActionMessageTranslationKey,
    ): string {
        if (translationKey === 'chatbot_context_file_generate_page_message') {
            return howspace.translate('chatbot_context_file_generate_page_message');
        }

        return howspace.translate('chatbot_context_file_create_widget_message');
    }

    private workspaceContextUploadTitle(item: WorkspaceContextUploadStatusItem): string {
        return item.title || item.filename || 'this file';
    }

    private workspaceContextUserVisibleErrorMessage(item: WorkspaceContextUploadStatusItem): string {
        const translationKey = workspaceContextUserVisibleErrorTranslationKeys[
            item.userVisibleError as keyof typeof workspaceContextUserVisibleErrorTranslationKeys
        ];
        if (translationKey) {
            return this.replaceWorkspaceContextErrorLimitPlaceholders(
                item.userVisibleError,
                howspace.translate(translationKey),
            );
        }

        return '';
    }

    private replaceWorkspaceContextErrorLimitPlaceholders(userVisibleError: string, message: string): string {
        const max = {
            source_file_too_large: this.options.labels.contextFileMaxSourceFileSize,
            too_many_assets: this.options.labels.contextFileMaxCreatedAssetCount,
            asset_too_large: this.options.labels.contextFileMaxCreatedAssetFileSize,
        }[userVisibleError] || '';

        return message.replace(/%max%/g, max);
    }

    private workspaceContextUploadStatusLabel(
        item: WorkspaceContextUploadStatusItem,
        userVisibleErrorMessage = '',
    ): string {
        const filename = item.filename || item.title || '';
        const template = {
            queued: this.options.labels.contextFileQueued || 'Queued %filename%',
            processing: this.options.labels.contextFileProcessing || 'Processing %filename%',
            ready: this.options.labels.contextFileReady || 'Ready %filename%',
            failed: this.options.labels.contextFileFailed || 'Could not process %filename%',
        }[item.status];
        const label = template.replace('%filename%', filename);
        return userVisibleErrorMessage !== '' ? `${label}: ${userVisibleErrorMessage}` : label;
    }
}

function statusAreaItemFingerprint(item: StatusAreaItem): string {
    return [
        item.key,
        item.tone,
        statusAreaLabelFingerprint(item.label),
        item.dismissible ? 'dismissible' : '',
        item.expandable ? 'expandable' : '',
        (item.actions ?? []).map(action => [
            action.label,
            action.disabled === true ? 'disabled' : '',
        ].join('\u0003')).join('\u0004'),
    ].join('\u0001');
}

function statusAreaLabelFingerprint(label: StatusAreaLabel): string {
    if (typeof label === 'string') {
        return label;
    }

    return label
        .map(part => (typeof part === 'string' ? part : (part.textContent || '')))
        .join('');
}
